import { PermissionId } from "../../../models/permission";

export interface PermissionGroup {
  label: string;
  permissionIds: PermissionId[];
  // Shown only while the work-steps feature flag is on for the workspace, so
  // admins elsewhere never see permissions for a feature they do not have.
  requiresWorkSteps?: boolean;
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: "Workspace",
    permissionIds: ["workspace_management"],
  },
  {
    label: "Audit",
    permissionIds: ["view_audit_logs", "manage_approvals"],
  },
  {
    label: "Controls",
    permissionIds: ["run_test", "edit_test", "edit_control"],
  },
  {
    label: "Work Steps",
    permissionIds: ["run_workstep_test", "edit_workstep_test", "edit_workstep"],
    requiresWorkSteps: true,
  },
];

export const PERMISSION_LABELS: Record<PermissionId, string> = {
  workspace_management: "Workspace Management",
  view_audit_logs: "View Audit Logs",
  manage_approvals: "Manage Approvals",
  run_test: "Perform Testing",
  edit_test: "Set Up Test",
  edit_control: "Edit Control",
  edit_workstep: "Edit Work Step",
  edit_workstep_test: "Set Up Work Step Test",
  run_workstep_test: "Perform Work Step Testing",
};

export const PERMISSION_DESCRIPTIONS: Record<PermissionId, string> = {
  run_test:
    "Do the testing work inside a test: add and delete samples, upload and remove evidence, run workpapers, edit results and conclusions, submit for review, and download exports.",
  edit_test:
    "Create, rename, reopen, and delete test sessions, and author their procedures, attributes, key reports, custom fields, and control fields.",
  edit_control:
    "Create, edit, and delete master controls (deleting one also deletes its test sessions), manage workpaper templates and field groups, and propagate improvements back to masters.",
  edit_workstep:
    "Create and edit master work steps, their attributes and testing tables, and import them from Optro.",
  edit_workstep_test:
    "Create, rename, reopen, and delete work step test sessions, and author their testing tables, attributes, and documents.",
  run_workstep_test:
    "Do the testing work inside a work step test: upload evidence, generate and edit results and workpapers, and submit for review.",
  workspace_management:
    "Invite and remove members, change roles, deactivate users, create or edit custom roles, and manage workspace integrations.",
  view_audit_logs:
    "Read the workspace's audit log of who did what, when, and against which resource.",
  manage_approvals:
    "Configure approval workflows and approve or reject pending review requests.",
};

const PERMISSION_VERBS: Record<PermissionId, string> = {
  workspace_management: "managing the workspace",
  view_audit_logs: "viewing audit logs",
  manage_approvals: "managing approvals",
  run_test: "performing testing",
  edit_test: "setting up this test",
  edit_control: "editing controls",
  edit_workstep: "editing work steps",
  edit_workstep_test: "setting up this work step test",
  run_workstep_test: "performing work step testing",
};

export function permissionDeniedTooltip(permission: PermissionId): string {
  return `Your role doesn't allow ${PERMISSION_VERBS[permission]}. Ask a workspace admin to update your role.`;
}
